import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FocusCard, useFocusSession } from '@/src/renderer/features/focus'
import { useTaskOperations } from '@/src/renderer/features/taskManagement/hooks/useTaskOperations'
import { useTransparentBackdrop } from '@/src/renderer/hooks/useTransparentBackdrop'
import './FocusWindow.scss'

/**
 * **Das Fokus-Fenster** — die Hülle um die {@link FocusCard} auf dem Desktop.
 *
 * Sie hält keinen Zustand: Was sie zeigt, liest {@link useFocusSession} über
 * `useDataSource()` aus derselben IndexedDB, in die das Hauptfenster schreibt
 * (ADR 0025). Beide Fenster laden dasselbe Bundle vom selben Origin, darum sehen
 * Dexies Live-Queries die Änderungen des jeweils anderen.
 *
 * **Nur das, was am Fenster hängt, steht hier:** der durchsichtige Grund, der
 * IPC-Weg `focus:end-requested` und das Erledigen. Alles andere teilt sie sich
 * seit 3d mit dem Web-Überzug.
 *
 * Sie liegt unter `windows/` und nicht in `features/focus`, weil hier zwei
 * Features zusammenkommen — das Fokus-Fenster und das Erledigen einer Aufgabe. Features
 * importieren keine Features; Hüllen auf App-Ebene dürfen beide einsetzen.
 */
export const FocusWindow = () => {
  const { t } = useTranslation()
  const { task, card, end, complete } = useFocusSession()

  useTransparentBackdrop()

  // `momentOrigin: 'focus'` — der Haken hier verdient den Geschafft-Moment
  // immer, ob eine Belohnung daranhängt oder nicht.
  const { handleToggleComplete } = useTaskOperations({
    t,
    tasks: task ? [task] : [],
    momentOrigin: 'focus',
  })

  const hovered = useCursorOverWindow()
  const measureRef = useFitWindowToCard()

  /*
    **Zuklappen, Sperren und ⌘W** enden alle hier. Der Main-Prozess schreibt die
    Zeit nicht selbst — sie steht in der IndexedDB, und die gehört dem Renderer.
    Er reicht nur den Zeitpunkt herüber: Gebucht wird bis zum Zuklappen, nicht
    bis zum Aufwachen.
  */
  useEffect(() => {
    if (!window.api?.receive) return
    return window.api.receive('focus:end-requested', (payload?: { at?: number }) => {
      void end({ at: payload?.at ? new Date(payload.at) : undefined, reason: 'suspended' })
    })
  }, [end])

  // Kein laufender Abschnitt: Das Fenster geht ohnehin gleich zu.
  if (!card) return null

  return (
    <div className="focus-window" ref={measureRef}>
      <FocusCard
        {...card}
        className={hovered ? 'focus-card--hover' : undefined}
        onComplete={() => void complete(handleToggleComplete)}
      />
    </div>
  )
}

/**
 * **Das Fenster so hoch wie die Karte.**
 *
 * Zwei Textzeilen, und der Titel darf umbrechen — eine feste Höhe ließe
 * unter dem Titel je nach Fall verschieden viel Luft. Im Entwurf sind es immer
 * 12px (Figma 5420-53424), also wird gemessen und angesagt.
 *
 * **`getBoundingClientRect` statt `offsetHeight`:** Das eine kennt Bruchteile,
 * das andere rundet — und ein gerundetes Viertel lässt unten eine Haarlinie
 * Glas stehen. Aufgerundet wird trotzdem, denn Fenster gibt es nur in ganzen
 * Punkten.
 *
 * Eine Null wird nie gemeldet: Solange nichts steht, bleibt das Fenster, wie es
 * ist (CLAUDE.md, TabStack-Falle — hier wäre es das leere erste Bild).
 */
function useFitWindowToCard() {
  /*
    **Callback-Ref und nicht `useRef`.** Solange kein Abschnitt läuft, gibt das
    Fenster `null` zurück — das Element gibt es dann nicht. Ein `useRef` wäre im
    ersten Effekt leer gewesen, und mit leerer Abhängigkeitsliste hätte er es
    nie wieder versucht: Das Fenster blieb auf seiner Starthöhe stehen
    (gemessen im E2E, 56 gewollt, 80 geblieben).
  */
  const [el, setEl] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!el || !window.api?.invoke) return

    const melden = () => {
      const hoehe = el.getBoundingClientRect().height
      if (hoehe > 0) void window.api?.invoke?.('focus:resize', Math.ceil(hoehe))
    }

    melden()
    const beobachter = new ResizeObserver(melden)
    beobachter.observe(el)
    return () => beobachter.disconnect()
  }, [el])

  return setEl
}

/**
 * **Steht der Zeiger über dem Fenster?** — vom Hauptprozess erfragt, nicht im
 * Renderer gemessen.
 *
 * Die Karte zieht das Fenster (`-webkit-app-region: drag`), und eine Ziehfläche
 * schaltet **alle** Mausereignisse darauf ab: Weder CSS-`:hover` noch
 * `mouseenter` erreichen sie. Der Pause-Knopf erschien darum erst, wenn man
 * genau seinen Bereich traf — den man vorher gar nicht sieht (gemeldet
 * 22.09.2026). Der Hauptprozess vergleicht Zeiger und Fenstergrenzen und sagt
 * Bescheid.
 *
 * **Nur hier, nicht an der Karte selbst.** Im **Fokus-Überzug** (Web) und in
 * Storybook gibt es keine Ziehfläche; dort tut es das gewöhnliche `:hover`.
 */
function useCursorOverWindow(): boolean {
  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    if (!window.api?.receive) return
    return window.api.receive('focus:hover', (inside: boolean) => setHovered(!!inside))
  }, [])

  return hovered
}
